import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTabCycle } from "../../keyboard/useTabCycle";
import {
  GetOrderbook200Response,
  GetOrders200Response,
  SideResponse,
} from "../../codegen-api";
import { IOptionMarket } from "../../contexts/MarketInstrumentContext/useGetMarkets";
import { useGetOptionData } from "../../hooks/api/statistics/useGetOptionData";
import { IWSSTicker } from "../../hooks/wss/model/ticker";
import useTradesWSS from "../../hooks/wss/useTradesWSS";
import { IPriceSize } from "../../interfaces/Orderbook";
import { AssetResponse } from "../../utils/asset";
import { getContractPriceStep } from "../../utils/instruments";
import { knownPrice } from "../../utils/unknownNumbers";
import InstrumentTradeHistory from "../InstrumentTradeHistory";
import { MarketData } from "../MarketData";
import { Tab } from "../OptionFilter/style";
import {
  OrderbookEmptyContent,
  OrderbookTabWrapper,
  PlaceholderContent,
} from "../TradeForm/style";
import { Select } from "../shared/Select";
import Transition from "../shared/Transition";
import { Greeks } from "./Greeks";
import PayoffChart from "./PayoffChart";
import TradeOrderbook from "./TradeOrderBook";
import { MarkRow } from "./TradeOrderBook/MarkRow";
import {
  OrderbookSelect,
  OrderbookWrapper,
  TicketGreeksWrapper,
} from "./style";

export interface ITicketDetailsProps {
  asset: AssetResponse;
  orderDirection: SideResponse;
  averagePremium: number;
  selectedInstrument?: IOptionMarket;
  totalSize: number;
  showPlaceholder?: boolean;
  mobileMode?: boolean;

  // ORDERBOOK
  orderbookData?: GetOrderbook200Response;
  orderData?: GetOrders200Response[];
  ticker?: IWSSTicker;
  indexPrice: number;
  // % away from index price that a trade is allowed
  indexPriceCollar: number;
  onOrderbookRowClick: (
    price: string,
    size: string,
    side: SideResponse
  ) => void;
}

export enum TicketDetailsPageEnum {
  ORDERBOOK = "orderbook",
  DATA = "data",
  PAYOFF = "payoff",
  TRADES = "trades",
}

function TicketDetails({
  asset,
  orderDirection,
  averagePremium,
  selectedInstrument,
  totalSize,
  showPlaceholder,
  orderbookData,
  orderData,
  ticker,
  indexPrice,
  indexPriceCollar,
  mobileMode,
  onOrderbookRowClick,
}: ITicketDetailsProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TicketDetails.TicketDetails",
  });

  const { data: optionData } = useGetOptionData(
    selectedInstrument?.instrument_name
  );
  const { data: histories, loading: tradeHistoryValidating } = useTradesWSS(
    selectedInstrument?.instrument_name
  );

  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const [selectedPage, setPage] = useState<TicketDetailsPageEnum>(
    mobileMode ? TicketDetailsPageEnum.DATA : TicketDetailsPageEnum.ORDERBOOK
  );
  const pageOptions: TicketDetailsPageEnum[] = useMemo(
    () => Object.values(TicketDetailsPageEnum) as TicketDetailsPageEnum[],
    []
  );

  // [ / ] switch the ticket sub-tab while the pointer is over this panel.
  const ticketGateRef = useTabCycle<HTMLDivElement>(
    pageOptions.length,
    pageOptions.indexOf(selectedPage),
    (i) => setPage(pageOptions[i]),
    { gated: true }
  );

  const bidAsks = useMemo(() => {
    let bids: IPriceSize[] = (orderbookData?.bids as IPriceSize[]) || [];
    let asks: IPriceSize[] = (orderbookData?.asks as IPriceSize[]) || [];

    const diff = bids.length - asks.length;
    if (diff > 0) {
      // Positive diff means bids > asks
      asks = [...asks, ...Array(Math.abs(diff)).fill(["0", "0"])];
    } else if (diff < 0) {
      bids = [...bids, ...Array(Math.abs(diff)).fill(["0", "0"])];
    }

    return {
      bids,
      asks,
    };
  }, [orderbookData]);

  useEffect(() => {
    setPage((prevPage) => {
      if (!pageOptions.includes(prevPage)) {
        return pageOptions[0];
      }
      return prevPage;
    });
  }, [pageOptions]);

  // Scroll to center of orderbook whenever new instrument is selected
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (container) {
      const scrollTop = container.scrollHeight / 2 - container.clientHeight / 2;
      container.scrollTo({
        behavior: "smooth",
        top: scrollTop,
      });
    }
  }, [selectedInstrument]);

  return (
    <OrderbookWrapper
      ref={ticketGateRef}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
    >
      <OrderbookSelect>
        <Select
          isRound
          options={pageOptions.map((opt) => ({
            label: t(opt),
            isActive: selectedPage === opt,
            onClick: () => setPage(opt),
          }))}
        />
      </OrderbookSelect>
      {showPlaceholder || !selectedInstrument ? (
        <PlaceholderContent>
          <span>{t("select_strike_price")}</span>
        </PlaceholderContent>
      ) : (
        <Transition key={selectedPage}>
          {selectedPage === TicketDetailsPageEnum.ORDERBOOK && (
            <OrderbookTabWrapper ref={scrollableContainerRef}>
              {selectedInstrument ? (
                <TradeOrderbook
                  indexPriceCollar={indexPriceCollar}
                  sizeDecimals={
                    getContractPriceStep(selectedInstrument).amount_precision
                  }
                  priceDecimals={
                    getContractPriceStep(selectedInstrument).price_precision
                  }
                  accountOrders={orderData || []}
                  middleRow={<MarkRow ticker={ticker} />}
                  instrumentId={selectedInstrument.instrument_name}
                  bids={bidAsks.bids}
                  asks={bidAsks.asks}
                  showIV={!!selectedInstrument.expiry}
                  onRowClick={onOrderbookRowClick}
                  markPrice={knownPrice(ticker?.mark?.price)}
                  indexPrice={indexPrice}
                />
              ) : (
                <OrderbookEmptyContent>
                  <Tab flashing isActive>
                    {t("select_strike_price")}
                  </Tab>
                </OrderbookEmptyContent>
              )}
            </OrderbookTabWrapper>
          )}
          {selectedPage === TicketDetailsPageEnum.DATA && (
            <TicketGreeksWrapper>
              <Greeks greeks={optionData?.greeks} />
              <MarketData market={optionData?.markets} asset={asset} />
            </TicketGreeksWrapper>
          )}
          {selectedPage === TicketDetailsPageEnum.PAYOFF && (
            <PayoffChart
              asset={asset}
              orderDirection={orderDirection}
              averagePremium={averagePremium}
              totalSize={totalSize}
              optionType={selectedInstrument.option_type}
              strike={selectedInstrument.strike}
              expiry={selectedInstrument.expiry}
            />
          )}
          {selectedPage === TicketDetailsPageEnum.TRADES && (
            <InstrumentTradeHistory
              trades={histories}
              loading={tradeHistoryValidating}
              sizeDecimals={
                getContractPriceStep(selectedInstrument).amount_precision
              }
              priceDecimals={
                getContractPriceStep(selectedInstrument).price_precision
              }
            />
          )}
        </Transition>
      )}
    </OrderbookWrapper>
  );
}

export default TicketDetails;
